/*
 * Homepage vehicle autocomplete — Phase 1 follow-up A Sub-phase C.
 *
 * Fixes vs Sub-phase B:
 *  - Loads the full chassis universe from /assets/ew-chassis-data.json
 *    (~286 records × 70 unique chassis). Replaces the prior Liquid
 *    data island that hit a metaobjects.values iteration cap (~22) and
 *    silently dropped M3 / G80 / MINI variants.
 *  - Tokenizes against `trim` field — "M3" lives on F80/G80/G81/G82
 *    chassis as a model value but "M3 Competition" / "M3 CS" live in
 *    trim. Trim gets engine-equivalent weight (10) since trim names
 *    are highly discriminating.
 *  - MINI is included. MINI chassis (F54/F55/F56/F57/F60) route to
 *    their OWN per-chassis pages /pages/{code} — exactly like BMW F
 *    cars, and the same targets the /pages/mini hub tiles link to.
 *  - Result-count discipline: render exactly N matches where N is
 *    real (capped at 6). No padding the dropdown with weak matches.
 *    Zero matches + typed input → empty dropdown + no-match off-ramp
 *    line. Empty input → popular-chassis chips (existing behavior).
 *
 * On selection: navigate to /pages/{chassis-handle}. Writes vehicle
 * + engine to window.EWVehicleResolver state.
 *
 * BUGS-1 Bug 3 — the matcher (scoreItem/tokenMatches/lev/WEIGHT) and the
 * suggestion formatter (fmtSuggestion) and the dataset fetch used to live
 * HERE. They are now the canonical shared API in ew-vehicle-resolver.js
 * (window.EWVehicleResolver.suggest/formatSuggestion/ready/data). This file
 * consumes that single module so the Garage dialog produces identical
 * suggestions. Render/keyboard/selection/routing are unchanged.
 */
(function () {
  'use strict';

  var widget = null;
  var input = null;
  var listbox = null;
  var noMatch = null;
  var popular = null;
  /* livery-cascade-fix — the [data-ew-capture] wrapper IFF this widget is
     the homepage capture cascade (livery-capture.liquid). Null for every
     other consumer of this snippet (the configurator chassis-picker
     fast-lane), which keeps the original navigate-on-select behavior. */
  var captureSection = null;
  var activeIndex = -1;
  var MAX_VISIBLE = 6;

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function resolver() { return window.EWVehicleResolver; }

  /* livery-cascade-fix — SINGLE source of truth for the chassis-page
     destination (formerly inlined in selectItem's navigation block).
     BMW + MINI E/F/G chassis → /pages/{chassis}; anything else →
     /pages/contact. MINI (F54/F55/F56/F57/F60) routes to its OWN
     per-chassis page just like BMW — those pages exist and are the same
     targets the /pages/mini hub's chassis tiles link to (ew-chassis-tiles
     → /pages/{code}); the old MINI→/pages/mini special-case was stale.
     Reads the already-persisted active vehicle, so the destination does
     NOT depend on the chosen livery — navigation is correct whatever order
     the click handlers run in. */
  function destForVehicle(v) {
    if (!v) return null;
    var code = String(v.code || '').toLowerCase();
    if (!code) return null;
    var first = code.charAt(0);
    if (first === 'e' || first === 'f' || first === 'g') return '/pages/' + code;
    return '/pages/contact';
  }

  function navigateToVehicle() {
    var R = window.EWVehicleResolver;
    var v = (R && typeof R.read === 'function') ? R.read() : null;
    var dest = destForVehicle(v);
    if (dest) window.location.href = dest;
  }

  function render(items) {
    if (!items.length) {
      listbox.hidden = true;
      listbox.innerHTML = '';
      $('[role="combobox"]', widget).setAttribute('aria-expanded', 'false');
      return;
    }
    /* Cap at MAX_VISIBLE; no padding — actual N <= 6 matches only */
    var capped = items.slice(0, MAX_VISIBLE);
    listbox.innerHTML = capped.map(function (it, idx) {
      var f = resolver().formatSuggestion(it);
      return '<li role="option" tabindex="-1" data-code="' + it.code + '" data-make="' + (it.make || '') + '" data-engine="' + (it.engine || '') + '" data-trim="' + (it.trim || '') + '" data-model="' + (it.model || '') + '" data-idx="' + idx + '" class="ew-hvi__option">' +
        '<span class="ew-hvi__option-line">' + f.line + '</span>' +
        '<span class="ew-hvi__option-meta">' + f.meta + '</span>' +
        '</li>';
    }).join('');
    listbox.hidden = false;
    $('[role="combobox"]', widget).setAttribute('aria-expanded', 'true');
    activeIndex = -1;
  }

  function search(query) {
    if (!query.trim()) {
      render([]);
      noMatch.hidden = true;
      popular.hidden = false;
      return;
    }
    popular.hidden = true;
    /* Canonical, shared matcher (scoring + sort + dedup by code|trim + 6-cap)
       lives in ew-vehicle-resolver.js so this surface is identical to the
       Garage dialog. */
    var items = resolver().suggest(query);
    render(items);
    /* Zero matches + typed input → empty dropdown + no-match off-ramp */
    noMatch.hidden = items.length > 0;
  }

  function selectItem(item, query) {
    if (!item || !item.code) return;

    /* livery-cascade-fix — collapse the autocomplete dropdown on selection.
       Before the cascade fix the page navigated immediately, so the open
       listbox was never seen; now the homepage capture stays on-page, so an
       un-closed dropdown covers the revealed livery step and the pick "feels"
       like a no-op. render([]) hides + empties the listbox and flips the
       combobox to aria-expanded="false". Also clear the no-match off-ramp. */
    render([]);
    if (noMatch) noMatch.hidden = true;

    /* F-05 — persist make/model/trim + the honest ys/ye BAND. A model year
       is persisted ONLY when the visitor typed one and it validates inside
       the matched row's band (EWVehicleResolver.yearFromQuery) — the band
       end is never styled as a model year. Listbox options carry only
       code/make/engine/trim attributes, so resolve the FULL dataset row
       (ys/ye/model) through the shared resolver first. Label comes from
       the canonical shared formatter. Guard defensively — the resolver is
       loaded globally but may not have parsed yet on an edge-cache race. */
    var R = window.EWVehicleResolver;
    var row = (R && typeof R.resolveByCodeTrim === 'function')
      ? (R.resolveByCodeTrim(item.code, item.trim, item.model) || item)
      : item;
    var v = {
      code: item.code,
      engine: row.engine || item.engine || '',
      make: row.make || item.make || '',
      model: row.model || item.model || '',
      trim: item.trim || '',
      ys: row.ys || '',
      ye: row.ye || '',
      source: 'homepage-input',
    };
    var typedYear = (R && typeof R.yearFromQuery === 'function')
      ? R.yearFromQuery(query || '', row)
      : null;
    if (typeof typedYear === 'number' && isFinite(typedYear)) {
      v.year = typedYear;
      v.year_known = true; /* provenance: user-typed, band-validated */
    }
    v.label = (R && typeof R.displayLabel === 'function')
      ? R.displayLabel(v)
      : [v.make, v.model, v.trim].filter(Boolean).join(' ');
    if (R && typeof R.write === 'function') {
      R.write(v);
    }
    document.dispatchEvent(new CustomEvent('ew:vehicle-changed', { detail: { code: item.code, engine: item.engine || '' } }));

    /* livery-cascade-fix — ONLY the homepage capture cascade defers
       navigation. There, selecting a vehicle just reveals the livery
       (use-case) step (driven by R.write → ew:vehicle-resolved in
       sections/livery-capture.liquid); navigation runs later from the
       livery card click wired in init(). Every other consumer of this
       widget (the configurator chassis-picker fast-lane) keeps the
       original navigate-on-select behavior. Destination is unchanged
       either way (see destForVehicle). */
    if (!captureSection) navigateToVehicle();
  }

  function selectByCode(code) {
    if (!code) return;
    /* A popular chip can be clicked before any typing/focus loaded data;
       resolve through the shared module's ready() so the lookup never
       silently no-ops on a cold cache. */
    resolver().ready().then(function () {
      var item = resolver().resolveByCode(code);
      /* Chips render on an EMPTY input — no typed query, no year. */
      if (item) selectItem(item, '');
    });
  }

  function selectFromOption(opt) {
    var item = {
      code: opt.getAttribute('data-code'),
      make: opt.getAttribute('data-make') || '',
      engine: opt.getAttribute('data-engine') || '',
      trim: opt.getAttribute('data-trim') || '',
      /* F-05 — model disambiguates a code+trim collision (G20 330i Base
         vs M340i Base) at resolution time. */
      model: opt.getAttribute('data-model') || '',
    };
    /* F-05 — capture the typed query at pick time so a year the visitor
       typed ("2021 330i") travels with the pick for band validation. */
    selectItem(item, input ? input.value : '');
  }

  function setActiveOption(delta) {
    var opts = $$('.ew-hvi__option', listbox);
    if (!opts.length) return;
    activeIndex = (activeIndex + delta + opts.length) % opts.length;
    opts.forEach(function (o, i) { o.classList.toggle('is-active', i === activeIndex); });
    var active = opts[activeIndex];
    if (active) active.scrollIntoView({ block: 'nearest' });
  }

  function init() {
    widget = $('[data-ew-hvi]');
    if (!widget) return;
    input = $('[data-ew-hvi-input]', widget);
    listbox = $('[data-ew-hvi-listbox]', widget);
    noMatch = $('[data-ew-hvi-no-match]', widget);
    popular = $('[data-ew-hvi-popular]', widget);

    /* livery-cascade-fix — in the homepage capture cascade, navigation is
       gated on the LIVERY select, not the vehicle select. When a use-case
       (livery) card inside THIS capture section is chosen, go to the
       chassis page. Scoped to the capture section so the shared use-case
       cards elsewhere (the sitewide modal, the Garage dialog) are
       unaffected; the "keep it neutral" skip ([data-ew-use-case-clear]) is
       not a card and does not navigate. The use-case is persisted
       synchronously on the same click (EWLivery.setUseCase) before the
       browser unloads. Only the capture-cascade widget registers this. */
    captureSection = widget.closest('[data-ew-capture]');
    if (captureSection) {
      document.addEventListener('click', function (e) {
        if (!e.target || !e.target.closest) return;
        var card = e.target.closest('.ew-uc-card[data-ew-use-case]');
        if (!card || !captureSection.contains(card)) return;
        navigateToVehicle();
      });
    }

    /* Load chassis data on first interaction to avoid blocking first paint.
       Refire current query once data lands (the shared module caches the
       fetch, so repeated ready() calls are cheap). */
    function ensureDataAndSearch(q) {
      resolver().ready().then(function () { search(q); });
    }

    var debounce;
    input.addEventListener('input', function () {
      clearTimeout(debounce);
      debounce = setTimeout(function () { ensureDataAndSearch(input.value); }, 80);
    });
    input.addEventListener('focus', function () {
      resolver().ready();
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActiveOption(1); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setActiveOption(-1); return; }
      if (e.key === 'Enter') {
        e.preventDefault();
        var opts = $$('.ew-hvi__option', listbox);
        var pick = (activeIndex >= 0 && opts[activeIndex]) ? opts[activeIndex] : opts[0];
        if (pick) selectFromOption(pick);
        return;
      }
      if (e.key === 'Escape') {
        input.value = '';
        render([]);
        noMatch.hidden = true;
        popular.hidden = false;
      }
    });

    document.addEventListener('click', function (e) {
      var opt = e.target.closest('.ew-hvi__option');
      if (opt && widget.contains(opt)) { selectFromOption(opt); return; }
      var chip = e.target.closest('[data-ew-hvi-pick]');
      if (chip && widget.contains(chip)) { selectByCode(chip.getAttribute('data-ew-hvi-pick')); return; }
      if (!widget.contains(e.target) && !listbox.hidden) {
        render([]);
      }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
